import React, { type ComponentType, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconAdd from '@/assets/icons/icon-add.svg?react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import IconMoreVertical from '@/assets/icons/icon-more-vertical.svg?react';

interface StyleFieldProps {
  text: string;
  className?: string;
  // Variant: simple (like SectionField) or withActions (screenshot design)
  variant?: 'simple' | 'withActions';
  // Simple variant extras
  imageSrc?: string;
  onClick?: () => void;
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  showBlock?: boolean;
  // Block appearance controls (used by both variants when a block is shown)
  blockVariant?: 'gradient' | 'solid';
  blockBgColor?: string; // for solid
  blockText?: string; // e.g., 'Aa'
  blockTextStyle?: React.CSSProperties; // control font family/size/weight via inline style
  // Custom gradient support when blockVariant = 'gradient'
  blockGradientClassName?: string; // Tailwind gradient utilities
  blockGradientStyle?: React.CSSProperties; // custom gradient via backgroundImage
  // Auto text color for solid/gradient if no explicit color provided in textStyle
  autoTextColor?: boolean;
  isActive?: boolean;
  showPublishedChip?: boolean;
  // withActions handlers
  onApply?: () => void;
  onEditStyle?: () => void;
  onRename?: () => void;
  onDuplicate?: () => void;
  onCopyStyleLink?: () => void;
  onRemoveStyle?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  // Brand profile context — controls dropdown label ("Edit Brand Kit" vs "Edit Style")
  isBrandProfileStyle?: boolean;
  // Inline rename support (when variant = 'withActions')
  isRenaming?: boolean;
  renameValue?: string;
  onRenameChange?: (value: string) => void;
  onRenameCommit?: () => void; // commit current renameValue
  onRenameCancel?: () => void;
  'data-qa-selector'?: string;
}

const FancyBlock: React.FC<{
  variant: 'gradient' | 'solid';
  solidBg?: string;
  gradientStyle?: React.CSSProperties;
  text?: string;
  textStyle?: React.CSSProperties;
  autoTextColor?: boolean;
}> = ({
  variant,
  solidBg = '#E5E7EB',
  gradientStyle,
  text = 'Aa',
  textStyle,
  autoTextColor = true,
}) => {
  // Small helper to decide readable text color for a bg color (hex or rgb)
  const getContrastingTextColor = (bg: string): string => {
    // try hex #RRGGBB or #RGB
    const hexMatch = bg.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    let r = 255,
      g = 255,
      b = 255;
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3)
        hex = hex
          .split('')
          .map((c) => c + c)
          .join('');
      const intVal = parseInt(hex, 16);
      r = (intVal >> 16) & 255;
      g = (intVal >> 8) & 255;
      b = intVal & 255;
    } else {
      // rgb/rgba fallback
      const rgbMatch = bg
        .replace(/\s+/g, '')
        .match(/^rgba?\((\d+),(\d+),(\d+)(?:,(0|0?\.\d+|1))?\)$/i);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10);
        g = parseInt(rgbMatch[2], 10);
        b = parseInt(rgbMatch[3], 10);
      }
    }
    // perceived luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? '#111827' : '#ffffff'; // neutral-900 vs white
  };

  if (variant === 'solid') {
    const computedColor =
      autoTextColor && !(textStyle && textStyle.color)
        ? getContrastingTextColor(solidBg)
        : undefined;

    // derive light tints from bg for overlays
    const parseHex = (hex: string) => {
      const m = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (!m) return { r: 0, g: 0, b: 0 };
      let h = m[1];
      if (h.length === 3)
        h = h
          .split('')
          .map((c) => c + c)
          .join('');
      const n = parseInt(h, 16);
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };
    const toRgba = (r: number, g: number, b: number, a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;
    const lighten = (hex: string, factor = 0.6, alpha = 0.2) => {
      const { r, g, b } = parseHex(hex);
      const lr = Math.round(r + (255 - r) * factor);
      const lg = Math.round(g + (255 - g) * factor);
      const lb = Math.round(b + (255 - b) * factor);
      return toRgba(lr, lg, lb, alpha);
    };

    const overlay1 = lighten(solidBg, 0.7, 0.1); // wide diagonal soft tint
    const overlay2 = lighten(solidBg, 0.5, 0.3); // right vertical softer tint

    return (
      <div
        className="relative flex h-8 min-h-8 w-8 min-w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md"
        style={{
          backgroundColor: solidBg,
        }}
      >
        {/* diagonal soft tint */}
        <div
          className="absolute top-3 left-3 h-8 w-7 origin-top-left rotate-45 rounded-full blur"
          style={{ backgroundColor: overlay1 }}
        />
        {/* right vertical tint */}
        <div
          className="absolute -top-2 left-12 h-8 w-5 origin-top-left rotate-90 rounded-full blur-sm"
          style={{ backgroundColor: overlay2 }}
        />

        <span
          className={clsx('z-2 text-[16px] leading-none font-medium')}
          style={{
            ...(computedColor ? { color: computedColor } : {}),
            ...(textStyle ?? {}),
          }}
        >
          {text}
        </span>
      </div>
    );
  }

  if (variant === 'gradient') {
    // This currently is set to hide decorative blobs when custom gradient provided. Fallback is used for Storybook demos.
    const hasCustomGradient = gradientStyle && gradientStyle.backgroundImage;

    return (
      <div
        className="relative flex h-8 min-h-8 w-8 min-w-8 items-center justify-center overflow-hidden rounded-md bg-white"
        style={{
          ...(gradientStyle ?? {}),
        }}
      >
        {!hasCustomGradient && (
          <>
            {/* orange tint blob */}
            <div className="absolute top-[15.01px] left-[-9.88px] h-8 w-8 origin-top-left rotate-[11.22deg] rounded-full bg-[rgba(253,213,186,0.75)] shadow-[inset_0_0_3.128px_rgba(73,54,236,1.00)] blur-[2px]" />
            {/* violet vertical blob */}
            <div className="absolute top-[-10.14px] left-[24.11px] h-9 w-8 origin-top-left rotate-90 rounded-full bg-[rgba(109,40,217,0.40)] shadow-[inset_0_0_3.128px_rgba(73,54,236,1.00)] blur-[2px]" />
            {/* cyan vertical blob */}
            <div className="absolute -top-[10px] left-[37.5px] h-8 w-8 origin-top-left rotate-90 rounded-full bg-[rgba(103,232,249,0.30)] shadow-[inset_0_0_3.128px_rgba(73,54,236,1.00)] blur-[2px]" />
            {/* blue tint blob */}
            <div className="absolute top-[8.3px] left-[13.03px] h-8 w-8 origin-top-left rotate-[11.22deg] rounded-full bg-[rgba(37,99,235,0.75)] shadow-[inset_0_0_3.128px_rgba(73,54,236,1.00)] blur-[5px]" />
          </>
        )}

        <span
          className={clsx('text-[16px] leading-none font-medium')}
          style={{
            color: (!textStyle || !textStyle.color) && autoTextColor ? '#ffffff' : undefined,
            zIndex: 2,
            ...(textStyle ?? {}),
          }}
        >
          {text}
        </span>
      </div>
    );
  }

  // fallback (shouldn't happen)
  return null;
};

export const StyleField: React.FC<StyleFieldProps> = ({
  className = '',
  text,
  variant = 'simple',
  // simple variant extras
  imageSrc,
  onClick,
  icon: Icon = IconAdd,
  showBlock = false,
  blockVariant = 'gradient',
  blockBgColor = '#E5E7EB',
  blockText = 'Aa',
  blockTextStyle,
  blockGradientStyle,
  autoTextColor = true,
  isActive = false,
  showPublishedChip = false,
  // actions variant handlers
  onApply,
  onEditStyle,
  onRename,
  onDuplicate,
  onCopyStyleLink,
  onRemoveStyle,
  onPublish,
  onUnpublish,
  isBrandProfileStyle = false,
  isRenaming = false,
  renameValue,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  'data-qa-selector': dataQaSelector,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const isRenamingRef = useRef<boolean>(isRenaming);
  const justEnteredRenameRef = useRef<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  useEffect(() => {
    isRenamingRef.current = isRenaming;
    if (isRenaming) {
      // Mark that we just entered rename mode
      justEnteredRenameRef.current = true;
      // Close the dropdown when entering rename mode
      setDropdownOpen(false);
      const timer = setTimeout(() => {
        justEnteredRenameRef.current = false;
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isRenaming]);
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Commit rename on first outside click without requiring initial input focus.
  // Use a capture-phase pointerdown listener that's always attached and consults a ref.
  useEffect(() => {
    const handlePointerDown = (event: Event) => {
      if (!isRenamingRef.current) return;
      if (justEnteredRenameRef.current) return;
      const el = containerRef.current;
      const target = event.target as Node | null;
      if (!el || !target) return;
      if (!el.contains(target)) {
        onRenameCommit?.();
      }
    };

    // capture ensures we see the event early regardless of other handlers
    const listenerOptions: AddEventListenerOptions = { capture: true };
    document.addEventListener('pointerdown', handlePointerDown, listenerOptions);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, listenerOptions);
    };
  }, [onRenameCommit]);
  if (variant === 'withActions') {
    return (
      <div
        ref={containerRef}
        data-qa-selector={dataQaSelector}
        className={clsx(
          'inline-flex h-12 w-56 items-center justify-between gap-2 rounded-lg p-2 text-neutral-500 outline outline-offset-[-1px] outline-transparent transition-all hover:bg-indigo-100',
          {
            'border border-indigo-200 bg-indigo-50': isActive,
            'outline-indigo-200': isActive,
          },
          className,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative">
            <FancyBlock
              variant={blockVariant}
              solidBg={blockBgColor}
              gradientStyle={blockGradientStyle}
              text={blockText}
              textStyle={blockTextStyle}
              autoTextColor={autoTextColor}
            />
            {showPublishedChip ? (
              <span className="absolute -top-0.5 -right-1 h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
            ) : null}
          </div>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              className="text-button-sm h-8 max-h-8 min-h-8 w-full rounded-md border border-indigo-500 bg-neutral-50 px-2 outline-none"
              value={renameValue ?? ''}
              onChange={(event) => onRenameChange?.(event.target.value)}
              onBlur={() => {
                // Don't commit on blur if we just entered rename mode
                if (!justEnteredRenameRef.current) {
                  onRenameCommit?.();
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onRenameCommit?.();
                if (event.key === 'Escape') onRenameCancel?.();
              }}
            />
          ) : (
            <div className="flex min-w-0 items-center">
              <span
                className={clsx('text-button-sm block truncate', {
                  'max-w-21': !isActive,
                  'max-w-32': isActive,
                })}
              >
                {text}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <IconSimpleButton size={28} icon={IconMoreVertical} state="default" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className={clsx(
                'bg-neutral-0 flex w-28 flex-col items-start overflow-hidden rounded-md p-1 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100',
              )}
              sideOffset={6}
              side="bottom"
              align="start"
            >
              <DropdownMenuItem
                className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                onSelect={onEditStyle}
              >
                <div className="text-xs leading-none font-medium text-neutral-900">
                  {isBrandProfileStyle ? 'Edit Brand Kit' : 'Edit Style'}
                </div>
              </DropdownMenuItem>
              {onRename && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={() => {
                    setDropdownOpen(false);
                    onRename();
                  }}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">Rename</div>
                </DropdownMenuItem>
              )}
              {onDuplicate && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={onDuplicate}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">Duplicate</div>
                </DropdownMenuItem>
              )}
              {onPublish && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={() => {
                    setDropdownOpen(false);
                    onPublish();
                  }}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">
                    Publish Style
                  </div>
                </DropdownMenuItem>
              )}
              {onUnpublish && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={() => {
                    setDropdownOpen(false);
                    onUnpublish();
                  }}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">
                    Unpublish Style
                  </div>
                </DropdownMenuItem>
              )}
              {onCopyStyleLink && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={onCopyStyleLink}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">
                    Copy Style link
                  </div>
                </DropdownMenuItem>
              )}
              {!isActive && onRemoveStyle && (
                <DropdownMenuItem
                  className="inline-flex h-8 w-full cursor-pointer items-center justify-start rounded-lg px-2 py-2"
                  onSelect={onRemoveStyle}
                >
                  <div className="text-xs leading-none font-medium text-neutral-900">
                    Remove Style
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {!isActive && onApply && (
            <SecondaryButton text="Apply" size="sm" state="default" icon={null} onClick={onApply} />
          )}
        </div>
      </div>
    );
  }

  // simple variant (SectionField-like)
  return (
    <div
      onClick={onClick}
      data-qa-selector={dataQaSelector}
      className={clsx(
        'group flex h-12 w-56 cursor-pointer items-center justify-between gap-2 rounded-lg p-2 text-neutral-500 outline outline-offset-[-1px] outline-transparent transition-all hover:bg-indigo-100',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {showBlock ? (
          <FancyBlock
            variant={blockVariant}
            solidBg={blockBgColor}
            gradientStyle={blockGradientStyle}
            text={blockText}
            textStyle={blockTextStyle}
            autoTextColor={autoTextColor}
          />
        ) : (
          imageSrc && (
            <img className="relative h-8 w-8 rounded-md transition-all" src={imageSrc} alt="icon" />
          )
        )}
        <span className="text-button-sm">{text}</span>
      </div>
      {Icon && <Icon className="h-4 w-4" />}
    </div>
  );
};

export default StyleField;
